import { useTinyHost } from "@tiny/host-contract";
import React from "react";
import {
  SearchField,
  SelectField,
  Button,
  Typography,
  Chip,
} from "@tiny/components";
import useDebounce from "@tiny/components/src/hooks/useDebounce";
import TrashFull from "@tiny/icons/generated/TrashFull";
import UploadOutline from "@tiny/icons/generated/UploadOutline";
import ChipWithTooltip from "@tiny/components/src/ChipWithTooltip/ChipWithTooltip";
import { sortSuppressionDetailsOptions } from "../SuppressionDetailsList.helpers";
import {
  CustomerStatuses,
  CustomerStatus,
} from "../SuppressionDetailsList.types";
import { useStyles } from "./SuppressionDetailsListHeader.styles";

interface SuppressionDetailsListHeaderProps {
  onDeleteClick: () => void;
  onDeselectAll: () => void;
  onExportClick: (type: string, columns?: string[]) => void;
  onSearchChange: (value: string) => void;
  onSortChange: (sort: string[]) => void;
  onColumnsSettingsOpen: () => void;
  onFilterOpen: () => void;
  onDeselectTag: (tag: string) => void;
  onDeselectStatus: (status: CustomerStatus) => void;
  selected: string[];
  sort: string[];
  tags: string[];
  statuses: CustomerStatuses;
  hideSort?: boolean;
  hideDelete?: boolean;
}

function SuppressionDetailsListHeader(
  props: SuppressionDetailsListHeaderProps
) {
  const {
    onDeleteClick,
    onDeselectAll,
    onExportClick,
    onSearchChange,
    onSortChange,
    selected,
    sort,
    tags,
    statuses,
    onDeselectTag,
    onDeselectStatus,
    hideSort,
    hideDelete,
  } = props;

  const classes = useStyles();

  const [search, setSearch] = React.useState("");

  const searchDebounced = useDebounce(search);

  const { capabilities } = useTinyHost();
  const actionDisabled = capabilities.actionsDisabled;

  const handleExport = (type: string, columns?: string[]) => {
    onExportClick(type, columns);
  };

  React.useEffect(() => {
    onSearchChange(searchDebounced);
  }, [onSearchChange, searchDebounced]);

  const shouldShowTagsAndStatuses = tags.length > 0 || statuses.length > 0;

  return (
    <>
      <div className={classes.root}>
        <div className={classes.wrap}>
          <div className={classes.top}>
            {selected.length > 0 ? (
              <div className={classes.selected}>
                {!hideDelete && (
                  <Button
                    variant="outlined"
                    color="red"
                    endIcon={<TrashFull />}
                    onClick={onDeleteClick}
                    className={classes.deleteButton}
                  >
                    Delete
                  </Button>
                )}
                <Button onClick={onDeselectAll} variant="text" noPadding>
                  Deselect all
                </Button>
                <Typography color="grey" tone={600} ml={5} variant="body2">
                  {selected.length} selected
                </Typography>
              </div>
            ) : (
              <div className={classes.search}>
                <SearchField
                  value={search}
                  placeholder="Search"
                  onChangeValue={setSearch}
                  className={classes.searchField}
                />
              </div>
            )}
          </div>
          <div className={classes.bottom}>
            <div className={classes.actions}>
              <Button
                disabled={actionDisabled}
                variant="text"
                noPadding
                disabledTooltip="Upgrade to Export"
                onClick={() => handleExport("CSV")}
                endIcon={<UploadOutline />}
              >
                Export
              </Button>
            </div>
            {!hideSort && (
              <div className={classes.sort}>
                <SelectField
                  options={sortSuppressionDetailsOptions}
                  value={sort}
                  onChangeValue={onSortChange}
                />
              </div>
            )}
          </div>
        </div>
        {shouldShowTagsAndStatuses && (
          <div className={classes.tags}>
            {statuses.map((status) => (
              <Chip
                key={status}
                label={status}
                color="grey"
                onDelete={() => onDeselectStatus(status)}
              />
            ))}
            {tags.map((tag) => (
              <ChipWithTooltip
                key={tag}
                label={tag}
                color="grey"
                onDelete={() => onDeselectTag(tag)}
                maxVisibleChars={16}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export default React.memo(SuppressionDetailsListHeader);
